// Dashboard (/): os textos das pendências, as declarações por tipo e os questionários
// pendentes do prestador. Cada número vem da mesma consulta da tela do atalho.

import { DECLARATION_TYPES } from "./declarations";
import { countPercentLabel } from "./quizAnswers";

/** "1 nota" / "4 notas" */
export const countText = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

const strong = (text) => `<strong>${text}</strong>`;

/**
 * Pendências do Dashboard da empresa, na ordem da tela. `counts` traz só o que
 * carregou (o que falhou fica de fora), e só entra o que tem número maior que zero.
 * `hours` é `{ text, contractors }` (as horas já formatadas, "12h30"); os outros,
 * números. `screen` é a tela do atalho, para o `canOpenScreen`.
 */
export function companyPendingItems(counts, competenceText) {
  const { hours, pendingInvoices, approvedInvoices, valueInputs, contractsWithoutValue, adjustments } = counts || {};
  const items = [];
  const one = (count) => count === 1;

  if (hours?.contractors > 0) {
    items.push({
      key: "hours",
      screen: "hoursApproval",
      route: "/c/hours-approval",
      action: "Aprovar horas",
      title: "Horas aguardando aprovação",
      text: `${strong(hours.text)} de ${countText(hours.contractors, "prestador", "prestadores")} em ${competenceText}.`,
    });
  }
  if (pendingInvoices > 0) {
    items.push({
      key: "pendingInvoices",
      screen: "taxInvoices",
      route: "/c/nfes",
      action: "Ver notas fiscais",
      title: "Notas fiscais para aprovar",
      text: `${strong(countText(pendingInvoices, "nota", "notas"))} ${one(pendingInvoices) ? "espera" : "esperam"} a aprovação.`,
    });
  }
  if (approvedInvoices > 0) {
    items.push({
      key: "approvedInvoices",
      screen: "taxInvoices",
      route: "/c/nfes",
      action: "Ver notas fiscais",
      title: "Notas aprovadas para pagar",
      text: one(approvedInvoices)
        ? `${strong("1 nota")} aprovada espera o pagamento.`
        : `${strong(`${approvedInvoices} notas`)} aprovadas esperam o pagamento.`,
    });
  }
  if (valueInputs > 0) {
    items.push({
      key: "valueInputs",
      screen: "paymentSchedules",
      route: "/c/contracts/value-approvals",
      action: "Aprovar valores",
      title: "Valores variáveis para aprovar",
      text: one(valueInputs)
        ? `${strong("1 valor")} informado espera a aprovação.`
        : `${strong(`${valueInputs} valores`)} informados esperam a aprovação.`,
    });
  }
  if (contractsWithoutValue > 0) {
    items.push({
      key: "contractsWithoutValue",
      screen: "contracts",
      route: "/c/contracts",
      action: "Ver contratos",
      title: "Valores variáveis do mês",
      text: one(contractsWithoutValue)
        ? `${strong("1 contrato")} variável sem valor em ${competenceText}.`
        : `${strong(`${contractsWithoutValue} contratos`)} variáveis sem valor em ${competenceText}.`,
    });
  }
  if (adjustments > 0) {
    items.push({
      key: "adjustments",
      screen: "timeClock",
      route: "/c/time-clock/adjustments",
      action: "Ver ajustes",
      title: "Ajustes de ponto",
      text: `${strong(countText(adjustments, "pedido", "pedidos"))} de ajuste ${one(adjustments) ? "espera" : "esperam"} a decisão.`,
    });
  }
  return items;
}

/**
 * Declarações por tipo (o `types` do GET /declarations/company-graph): os tipos do
 * back na ordem deles, com os zeros, e os de fora (dado antigo) no fim.
 */
export function declarationTypeBars(types) {
  const counts = types || {};
  const known = DECLARATION_TYPES.map((type) => type.value);
  const labels = [...known, ...Object.keys(counts).filter((label) => !known.includes(label))];
  const total = labels.reduce((sum, label) => sum + (Number(counts[label]) || 0), 0);
  const bars = labels.map((label) => {
    const count = Number(counts[label]) || 0;
    const percent = total ? Math.round((count / total) * 100) : 0;
    return { label, count, percent, text: countPercentLabel({ count, percent }) };
  });
  return { total, bars };
}

/**
 * Questionários que o prestador ainda não respondeu, entre os que a lista trouxe (o
 * back só devolve os ativos, com o `totalAnswers` dele). `exact` diz se a lista veio
 * inteira: senão, o aviso fica sem o número.
 */
export function pendingQuizzes(list, totalRegisters) {
  const quizzes = Array.isArray(list) ? list : [];
  const count = quizzes.filter((quiz) => !(Number(quiz.totalAnswers) > 0)).length;
  return { count, exact: (Number(totalRegisters) || 0) <= quizzes.length };
}

/** Texto do aviso de questionários; vazio quando não há o que responder. */
export function pendingQuizzesText({ count, exact }) {
  if (count === 0) return "";
  if (!exact) return "Você tem questionários para responder.";
  return `Você tem ${strong(countText(count, "questionário", "questionários"))} para responder.`;
}
